import React, { useState, useEffect } from "react";
import {
  Users,
  Calendar,
  ArrowRight,
  DollarSign,
  AlertCircle,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Eye,
} from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Student } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import { ENTITY_NAMES } from "../constants/entities";
import {
  startOfToday,
  format,
  parseISO,
  isBefore,
  startOfWeek,
  addDays,
} from "date-fns";

interface LabDate {
  id: string;
  lab_number: number;
  date: string;
}

interface StudentWithRun extends Student {
  run?: {
    name: string;
    series?: {
      id: string;
      title: string;
      code: string;
    };
  };
}

interface RunWithDates {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
  location: string;
  series: {
    id: string;
    title: string;
    code: string;
    labs?: {
      id: string;
      order_number: number;
      title?: string;
    }[];
  };
  lab_dates: LabDate[];
  color?: string;
}

const Dashboard: React.FC = () => {
  const [totalStudents, setTotalStudents] = useState<number>(0);
  const [recentStudents, setRecentStudents] = useState<StudentWithRun[]>([]);
  const [activeRuns, setActiveRuns] = useState<number>(0);
  const [totalRevenue, setTotalRevenue] = useState<number>(0);
  const [outstandingPayments, setOutstandingPayments] = useState<number>(0);
  const [nextLabs, setNextLabs] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allRuns, setAllRuns] = useState<RunWithDates[]>([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isCalendarLoading, setIsCalendarLoading] = useState(false);
  const { user } = useAuth();

  const formatTime = (timeString: string): string => {
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const timeToIndex = (time: string) => {
    const hour = parseInt(time.split(":")[0], 10);
    return hour - 8; // 8 AM is index 0
  };

  const timeSlots = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 8; // Starting from 8 AM
    return `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? "PM" : "AM"}`;
  });

  const getColorClasses = () => {
    return {
      bg: "bg-primary-50",
      border: "border-primary-200",
      text: "text-primary-800",
    };
  };

  const navigateWeek = (direction: "prev" | "next") => {
    setCurrentDate((current) => {
      const newDate = new Date(current);
      newDate.setDate(current.getDate() + (direction === "next" ? 7 : -7));
      return newDate;
    });
  };

  const fetchCalendarData = async () => {
    setIsCalendarLoading(true);
    try {
      const { data: runsData } = await supabase
        .from("runs")
        .select(
          `
          *,
          series:series_id (
            id,
            title,
            code,
            labs (
              id,
              order_number,
              title
            )
          ),
          lab_dates (
            id,
            lab_number,
            date
          )
        `
        )
        .eq("active", true);

      if (runsData) {
        setAllRuns(runsData);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setIsCalendarLoading(false);
    }
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);

        // Get total students count
        const { count } = await supabase
          .from("students")
          .select("*", { count: "exact", head: true });

        setTotalStudents(count || 0);

        // Get recent students with their run info
        const { data: recentData } = await supabase
          .from("students")
          .select(
            `
            *,
            run:run_id (
              name,
              series:series_id (
                id,
                title,
                code
              )
            )
          `
          )
          .order("created_at", { ascending: false })
          .limit(5);

        if (recentData) {
          setRecentStudents(recentData);
        }

        // Get active runs count
        const { count: runsCount } = await supabase
          .from("runs")
          .select("*", { count: "exact", head: true })
          .eq("active", true);

        setActiveRuns(runsCount || 0);

        // Get next labs across all active runs
        const { data: allLabDates } = await supabase
          .from("runs")
          .select(
            `
            id,
            name,
            start_time,
            end_time,
            location,
            series:series_id (
              id,
              title,
              code,
              labs (
                id,
                order_number
              )
            ),
            lab_dates (
              id,
              lab_number,
              date
            )
          `
          )
          .eq("active", true);

        if (allLabDates) {
          const today = startOfToday();

          // Flatten all lab dates and associate them with their run information
          const nextLabsData = allLabDates
            .flatMap((run: any) => {
              return (run.lab_dates || [])
                .filter(
                  (date: LabDate) => !isBefore(parseISO(date.date), today)
                )
                .map((date: LabDate) => ({
                  ...run,
                  nextLabNumber: date.lab_number,
                  nextLabDate: date.date,
                  nextLabId: run.series?.labs?.find(
                    (lab: { id: string; order_number: number }) =>
                      lab.order_number === date.lab_number
                  )?.id,
                }));
            })
            .sort(
              (a, b) =>
                parseISO(a.nextLabDate).getTime() -
                parseISO(b.nextLabDate).getTime()
            )
            .slice(0, 5); // Take only the next 5 upcoming labs

          setNextLabs(nextLabsData);
        }

        // Get payment statistics
        const { data: activeRuns } = await supabase
          .from("runs")
          .select(
            `
            id,
            price,
            students (id),
            payments (amount)
          `
          )
          .eq("active", true);

        if (activeRuns) {
          let totalRevenue = 0;
          let totalExpected = 0;

          activeRuns.forEach((run) => {
            const expectedFromRun =
              (run.students?.length || 0) * (run.price || 0);
            totalExpected += expectedFromRun;

            const paidInRun =
              run.payments?.reduce(
                (sum: number, payment: { amount: number }) =>
                  sum + payment.amount,
                0
              ) || 0;
            totalRevenue += paidInRun;
          });

          setTotalRevenue(totalRevenue);
          setOutstandingPayments(Math.max(0, totalExpected - totalRevenue));
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  const StatCard = ({
    icon: Icon,
    title,
    value,
    bgColor,
  }: {
    icon: any;
    title: string;
    value: string;
    bgColor: string;
  }) => (
    <div className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200">
      <div className="flex items-start">
        <div className={`rounded-full ${bgColor} p-3 mr-4`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <p className="">{title}</p>
          <h3 className="mt-1">{value}</h3>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="">Dashboard</h1>
          <p className="mt-1">Welcome back, {user?.email}</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link to="/students/add" className="btn btn-primary">
            Add New Student
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-white rounded-lg shadow-sm p-6 border border-neutral-200 animate-pulse"
            >
              <div className="flex items-start">
                <div className="rounded-full bg-neutral-200 p-3 mr-4 h-12 w-12"></div>
                <div className="w-full">
                  <div className="h-4 bg-neutral-200 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-neutral-200 rounded w-1/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              icon={Users}
              title="Total Students"
              value={totalStudents.toString()}
              bgColor="bg-secondary"
            />
            <StatCard
              icon={Calendar}
              title={`Active ${ENTITY_NAMES.RUN}s`}
              value={activeRuns.toString()}
              bgColor="bg-accentAlt"
            />
            <StatCard
              icon={DollarSign}
              title="Total Revenue"
              value={`$${totalRevenue.toFixed(2)}`}
              bgColor="bg-secondary"
            />
            <StatCard
              icon={AlertCircle}
              title="Outstanding"
              value={`$${outstandingPayments.toFixed(2)}`}
              bgColor="bg-accent"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-neutral-200">
                <h2 className="">Upcoming Sessions</h2>
              </div>
              <div className="divide-y divide-neutral-200">
                {nextLabs.length > 0 ? (
                  nextLabs.map((run) => (
                    <div
                      key={`${run.id}-${run.nextLabNumber}-${run.nextLabDate}`}
                      className="px-6 py-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="text-sm">
                          <Link
                            to={`/series/${run.series.id}/labs/${run.nextLabId}`}
                            className="text-sm font-medium"
                          >
                            Session {run.nextLabNumber}
                          </Link>{" "}
                          of{" "}
                          <Link
                            to={`/series/${run.series.id}`}
                            className="text-sm font-medium"
                          >
                            {run.series.code}
                          </Link>{" "}
                          -{" "}
                          <Link
                            to={`/runs/${run.id}`}
                            className="text-sm font-medium"
                          >
                            {ENTITY_NAMES.RUN} {run.name}
                          </Link>
                          <div className="flex mt-1 text-sm text-neutral-700">
                            <Calendar className="h-4 w-4 mr-1 flex-shrink-0" />
                            {format(
                              parseISO(run.nextLabDate),
                              "MMM d, yyyy"
                            )} • {formatTime(run.start_time)} -{" "}
                            {formatTime(run.end_time)}
                          </div>
                          <div className="flex mt-1 text-sm text-neutral-700">
                            <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                            {run.location}
                          </div>
                        </div>
                        <Link
                          to={`/series/${run.series.id}/labs/${run.nextLabId}`}
                          className="icon-view"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-8 text-center">
                    <p className="text-neutral-500">
                      No upcoming sessions scheduled
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
              <div className="px-6 py-5 border-b border-neutral-200">
                <h2 className="">Recent Students</h2>
              </div>
              <div className="divide-y divide-neutral-200">
                {recentStudents.length > 0 ? (
                  recentStudents.map((student) => (
                    <div
                      key={student.id}
                      className="px-6 py-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <Link to={`/students/${student.id}`} className="">
                            <p className="text-sm font-medium">
                              {student.codername}
                            </p>
                          </Link>
                          <div className="text-sm text-neutral-700">
                            <Link
                              to={`/runs/${student.run_id}`}
                              className="hover:text-primary-600"
                            >
                              {ENTITY_NAMES.RUN} {student.run?.name}
                            </Link>{" "}
                            of{" "}
                            <Link
                              to={`/series/${student.run?.series?.id}`}
                              className="hover:text-primary-600"
                            >
                              {student.run?.series?.code}
                            </Link>
                          </div>
                        </div>
                        <Link
                          to={`/students/${student.id}`}
                          className="icon-view"
                        >
                          <Eye className="h-5 w-5" />
                        </Link>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-8 text-center">
                    <p className="text-neutral-700">No students added yet</p>
                    <Link
                      to="/students/add"
                      className="mt-2 inline-flex items-center text-primary-600 hover:text-primary-700"
                    >
                      Add your first student
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                )}
              </div>
              <div className="bg-neutral-50 px-6 py-3 border-t border-neutral-200">
                <Link to="/students" className="btn btn-primary">
                  View all students
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden mb-8">
            <div className="px-6 py-5 border-b border-neutral-200 flex justify-between items-center">
              <h2 className="">Weekly Schedule</h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigateWeek("prev")}
                  className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                  aria-label="Previous week"
                  disabled={isCalendarLoading}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  onClick={() => navigateWeek("next")}
                  className="p-1 rounded hover:bg-neutral-100 text-neutral-600"
                  aria-label="Next week"
                  disabled={isCalendarLoading}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="relative min-w-full overflow-x-auto">
              {isCalendarLoading ? (
                <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50">
                  <div className="animate-pulse text-neutral-500">
                    Loading...
                  </div>
                </div>
              ) : null}

              <div className="grid grid-cols-[auto,repeat(7,minmax(140px,1fr))]">
                {/* Time column header */}
                <div className="sticky left-0 z-20 p-4 bg-neutral-50 border-b border-r border-neutral-200" />

                {/* Day headers */}
                {Array.from({ length: 7 }, (_, i) => {
                  const day = addDays(startOfWeek(currentDate), i);
                  return (
                    <div
                      key={i}
                      className="p-4 text-center border-b border-neutral-200 bg-neutral-50"
                    >
                      <div className="font-medium text-sm">
                        {format(day, "EEEE")}
                      </div>
                      <div className="text-sm text-neutral-500">
                        {format(day, "MMM d")}
                      </div>
                    </div>
                  );
                })}

                {/* Time slots */}
                {timeSlots.map((time, timeIndex) => (
                  <React.Fragment key={timeIndex}>
                    {/* Time label */}
                    <div className="sticky left-0 z-10 p-4 bg-white border-r border-neutral-200">
                      <div className="text-sm text-neutral-500">{time}</div>
                    </div>

                    {/* Time slots for each day */}
                    {Array.from({ length: 7 }, (_, dayIndex) => {
                      const currentDay = addDays(
                        startOfWeek(currentDate),
                        dayIndex
                      );
                      const dateStr = format(currentDay, "yyyy-MM-dd");

                      return (
                        <div
                          key={dayIndex}
                          className="relative p-2 border-b border-r border-neutral-200 min-h-[80px]"
                        >
                          {allRuns.map((run) => {
                            const labForThisDay = run.lab_dates?.find(
                              (labDate) => labDate.date === dateStr
                            );

                            if (
                              timeIndex === timeToIndex(run.start_time) &&
                              labForThisDay
                            ) {
                              const colors = getColorClasses();
                              const lab = run.series.labs?.find(
                                (l) =>
                                  l.order_number === labForThisDay.lab_number
                              );

                              return (
                                <div
                                  key={`${run.id}-${dateStr}`}
                                  className="group relative"
                                >
                                  <Link
                                    to={`/series/${run.series.id}/labs/${lab?.id}`}
                                  >
                                    <div
                                      className={`px-3 py-2 rounded-lg border ${colors.bg} ${colors.border} hover:shadow-sm transition-shadow duration-200`}
                                    >
                                      <div className="text-sm font-medium">
                                        Session {labForThisDay.lab_number}
                                      </div>
                                      <div className="flex flex-col text-xs text-neutral-500 mt-0.5 space-y-0.5">
                                        <span>{run.series?.code}</span>
                                        <span>
                                          {ENTITY_NAMES.RUN} {run.name}
                                        </span>
                                      </div>
                                    </div>
                                  </Link>
                                </div>
                              );
                            }
                            return null;
                          })}
                        </div>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="px-6 py-4 bg-neutral-50 border-t border-neutral-200">
              <p className="text-sm text-neutral-500">
                This schedule displays all active{" "}
                {ENTITY_NAMES.RUN.toLowerCase()}s. Click on a session to view
                details.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dashboard;
